import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { NAV, hasCap } from "../lib/nav.js";
import { useAuth } from "../context/AuthContext.jsx";

const COLLAPSE_KEY = "shellNavCollapsed";

/* Pick exactly ONE active nav item for a path: the entry with the LONGEST href
 * that the path is under. Without this, NavLink's default prefix matching lit
 * BOTH "/ma-trading" and "/ma-trading/positions" on the positions page (two
 * highlighted items). Longest-match means /ma-trading/positions wins there,
 * while /ma-trading/bot/9 still highlights /ma-trading. */
function activeKeyFor(pathname) {
  let best = null;
  for (const n of NAV) {
    const under =
      n.href === "/"
        ? pathname === "/"
        : pathname === n.href || pathname.startsWith(n.href + "/");
    if (under && (!best || n.href.length > best.href.length)) best = n;
  }
  return best ? best.key : null;
}

/* The left sidenav — ports shell.js sidenavHTML + its collapse / mobile-drawer
 * wiring. Cap-gates links off the auth cap set. `collapsed`/`setCollapsed` and
 * the mobile drawer state are lifted to Shell so the body padding can react. */
export default function Sidenav({ collapsed, setCollapsed, drawerOpen, setDrawerOpen }) {
  const { me, caps, logout } = useAuth();
  const { pathname } = useLocation();
  const activeKey = activeKeyFor(pathname);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
  };

  return (
    <>
      <aside className={"sidenav" + (drawerOpen ? " open" : "")} id="sidenav">
        <div className="sidenav-head">
          <Link className="brand" to="/" title="Home">
            <span className="logo">▦</span>
            <div className="brand-text">
              <div className="title">Liquidation Heatmap</div>
              <div className="subtitle">near-realtime</div>
            </div>
          </Link>
          <button
            className="nav-collapse"
            type="button"
            aria-label={collapsed ? "Expand menu" : "Collapse menu"}
            title={collapsed ? "Expand menu" : "Collapse menu"}
            onClick={toggleCollapse}
          >
            {collapsed ? "›" : "‹"}
          </button>
        </div>

        <nav className="nav" onClick={() => setDrawerOpen(false)}>
          {NAV.filter((n) => hasCap(caps, n.cap)).map((n) => (
            <NavLink
              key={n.key}
              to={n.href}
              title={n.label}
              className={n.key === activeKey ? "on" : undefined}
            >
              <span className="ico" aria-hidden="true">
                {n.ico || "•"}
              </span>
              <span className="lbl">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidenav-foot">
          <span className="dot" aria-hidden="true" />
          <Link className="whoami" to="/account" title="Your account">
            {me ? `${me.user} · ${me.role || ""}` : "—"}
          </Link>
          <button className="logout" type="button" title="Sign out" onClick={logout}>
            ⎋
          </button>
        </div>
      </aside>

      <button
        className="nav-toggle"
        type="button"
        aria-label="Open menu"
        onClick={(e) => {
          e.stopPropagation();
          setDrawerOpen(true);
        }}
      >
        ☰
      </button>
      <div
        className={"sidenav-backdrop" + (drawerOpen ? " open" : "")}
        onClick={() => setDrawerOpen(false)}
      />
    </>
  );
}

/* Read the persisted collapse flag once at module load for a flash-free start. */
export function initialCollapsed() {
  return localStorage.getItem(COLLAPSE_KEY) === "1";
}
